/**
 * Comparative Analysis Component
 * Long vs Short performance table
 */

import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { TradeStatisticsFilters, TradeStatistics, ComparativeAnalysisResponse } from "../../../../types/trade";
import { useComparativeAnalysis } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { DataTable } from "../../../../components/ui/data-table";
import {
  DashboardWidgetLoadingCard,
} from "../../Dashboard/components/DashboardWidgetLoadingCard";
import { StatisticsCardHeadline, STATS_MUTED_TEXT_CLASS } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";

interface ComparativeAnalysisProps {
  filters?: TradeStatisticsFilters;
  statistics?: TradeStatistics | null;
  /** When parent already fetched comparative analysis, avoid empty-first paint on tab mount. */
  dataOverride?: ComparativeAnalysisResponse | null;
}

interface LongShortRow {
  metric: string;
  long: string;
  short: string;
}

const ComparativeAnalysis: React.FC<ComparativeAnalysisProps> = ({
  filters,
  dataOverride,
}) => {
  const { formatMoney } = useDisplayCurrency();

  const { data: fetchedData, loading, error, errorAction } = useComparativeAnalysis(filters, {
    enabled: dataOverride === undefined,
  });
  const data = dataOverride ?? fetchedData;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    dataOverride ? null : error,
    dataOverride ? null : errorAction
  );

  const longShortPerformance = useMemo(() => {
    if (!data || !data.long_short) {
      return {
        long: { winRate: 0, totalPnL: 0, avgPnL: 0, trades: 0, winningTrades: 0, losingTrades: 0 },
        short: { winRate: 0, totalPnL: 0, avgPnL: 0, trades: 0, winningTrades: 0, losingTrades: 0 },
      };
    }

    const longTrades = data.long_short.long_trades || 0;
    const shortTrades = data.long_short.short_trades || 0;
    const longAvgPnL = data.long_short.average_long_pnl || 0;
    const shortAvgPnL = data.long_short.average_short_pnl || 0;
    const longTotalPnL = longAvgPnL * longTrades;
    const shortTotalPnL = shortAvgPnL * shortTrades;
    const longWinRate = data.long_short.long_win_rate ?? 0;
    const shortWinRate = data.long_short.short_win_rate ?? 0;
    const winningLong = data.long_short.winning_long_trades || 0;
    const losingLong = data.long_short.losing_long_trades || 0;
    const winningShort = data.long_short.winning_short_trades || 0;
    const losingShort = data.long_short.losing_short_trades || 0;

    return {
      long: { winRate: longWinRate, totalPnL: longTotalPnL, avgPnL: longAvgPnL, trades: longTrades, winningTrades: winningLong, losingTrades: losingLong },
      short: { winRate: shortWinRate, totalPnL: shortTotalPnL, avgPnL: shortAvgPnL, trades: shortTrades, winningTrades: winningShort, losingTrades: losingShort },
    };
  }, [data]);

  const tableData: LongShortRow[] = useMemo(() => {
    const totalTrades = longShortPerformance.long.trades + longShortPerformance.short.trades;
    const longPct = totalTrades > 0 ? ((longShortPerformance.long.trades / totalTrades) * 100).toFixed(1) : "0.0";
    const shortPct = totalTrades > 0 ? ((longShortPerformance.short.trades / totalTrades) * 100).toFixed(1) : "0.0";
    const longWLRatio = longShortPerformance.long.losingTrades > 0
      ? (longShortPerformance.long.winningTrades / longShortPerformance.long.losingTrades).toFixed(2)
      : longShortPerformance.long.winningTrades > 0 ? "∞" : "0.00";
    const shortWLRatio = longShortPerformance.short.losingTrades > 0
      ? (longShortPerformance.short.winningTrades / longShortPerformance.short.losingTrades).toFixed(2)
      : longShortPerformance.short.winningTrades > 0 ? "∞" : "0.00";

    return [
      {
        metric: "Win Rate",
        long: `${longShortPerformance.long.winRate.toFixed(2)}%`,
        short: `${longShortPerformance.short.winRate.toFixed(2)}%`,
      },
      {
        metric: "Total P&L",
        long: formatMoney(longShortPerformance.long.totalPnL),
        short: formatMoney(longShortPerformance.short.totalPnL),
      },
      {
        metric: "Avg P&L",
        long: formatMoney(longShortPerformance.long.avgPnL),
        short: formatMoney(longShortPerformance.short.avgPnL),
      },
      {
        metric: "Total Trades",
        long: String(longShortPerformance.long.trades),
        short: String(longShortPerformance.short.trades),
      },
      {
        metric: "Winning Trades",
        long: String(longShortPerformance.long.winningTrades),
        short: String(longShortPerformance.short.winningTrades),
      },
      {
        metric: "Losing Trades",
        long: String(longShortPerformance.long.losingTrades),
        short: String(longShortPerformance.short.losingTrades),
      },
      {
        metric: "Win/Loss Ratio",
        long: longWLRatio,
        short: shortWLRatio,
      },
      {
        metric: "% of Total Trades",
        long: `${longPct}%`,
        short: `${shortPct}%`,
      },
    ];
  }, [longShortPerformance, formatMoney]);

  const columns: ColumnDef<LongShortRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={cn(STATS_MUTED_TEXT_CLASS, "block truncate")}>
            {row.getValue("metric")}
          </span>
        ),
      },
      {
        accessorKey: "long",
        header: "Long",
        enableSorting: false,
        meta: { align: "end" },
        cell: ({ row }) => (
          <span className={cn(STATS_MUTED_TEXT_CLASS, "tabular-nums")}>
            {row.getValue("long")}
          </span>
        ),
      },
      {
        accessorKey: "short",
        header: "Short",
        enableSorting: false,
        meta: { align: "end" },
        cell: ({ row }) => (
          <span className={cn(STATS_MUTED_TEXT_CLASS, "tabular-nums")}>
            {row.getValue("short")}
          </span>
        ),
      },
    ],
    [],
  );

  const showTableSkeleton = loading && data == null;

  if (showTableSkeleton) {
    return (
      <DashboardWidgetLoadingCard
        cardType="longVsShortPerformance"
        variant="table"
        className="h-full min-h-0 flex-1"
      />
    );
  }

  if (showWidgetError && widgetError) {
    return (
      <div
        data-statistics-shadcn-card
        className="flex h-full min-h-0 flex-1 flex-col rounded-[6px] border border-border bg-background p-4"
      >
        <StatisticsCardHeadline>Long vs Short Performance</StatisticsCardHeadline>
        <div className="mt-3">
          <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />
        </div>
      </div>
    );
  }

  return (
    <div
      data-statistics-shadcn-card
      data-statistics-comparative-table=""
      className="flex h-full min-h-0 flex-1 flex-col rounded-[6px] border border-border bg-background p-4"
    >
      <StatisticsCardHeadline>Long vs Short Performance</StatisticsCardHeadline>
      <div className="mt-3 min-h-[296px]">
        <DataTable
          columns={columns}
          data={tableData}
          showPagination={false}
          emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
          tableClassName="[&_[data-slot=table]]:table-fixed [&_[data-slot=table]]:w-full"
        />
      </div>
    </div>
  );
};

export default ComparativeAnalysis;

